import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, MessageCircle, Phone } from "lucide-react";
import PageHead from "../components/PageHead";
import { Reveal, SectionHead } from "../components/Reveal";
import CtaBand from "../components/CtaBand";
import { HUBS, SITE, TRAILERS, waLink } from "../lib/site";

export default function Trailers() {
    return (
        <>
            <PageHead
                eyebrow="Trailer Availability"
                title="40FT | 50FT | 60FT"
                hindi="Trailers available across cities"
                intro="Flatbeds, semilowbeds and lowbeds placed across different locations in India. Availability changes daily — call or WhatsApp for live placement at your ramp."
                testid="trailers-page"
            />

            <section className="pb-20 lg:pb-28" data-testid="trailers-fleet-section">
                <div className="container-x grid gap-6 lg:grid-cols-3">
                    {TRAILERS.map((t, i) => (
                        <Reveal key={t.size} delay={i * 0.08} className="h-full">
                            <div
                                data-testid={t.testid}
                                className="group flex h-full flex-col rounded-3xl border border-line bg-white p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-ink hover:shadow-xl hover:shadow-ink/5 sm:p-8"
                            >
                                <div className="flex items-start justify-between">
                                    <p className="font-display text-5xl font-extrabold tracking-tight text-ink group-hover:text-brand sm:text-6xl">
                                        {t.size}
                                    </p>
                                    <span className="mt-2 rounded-full bg-[#10B981]/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.15em] text-[#0d9668]">
                                        On call
                                    </span>
                                </div>
                                <p className="mt-2 text-xs font-semibold uppercase tracking-[0.2em] text-fog">
                                    {t.name} — {t.type}
                                </p>
                                <p className="mt-4 font-hindi text-sm text-steel">{t.hindi}</p>
                                <p className="mt-4 text-sm leading-relaxed text-steel">{t.desc}</p>
                                <ul className="mt-5 space-y-2 border-t border-line pt-5">
                                    {t.cargo.map((c) => (
                                        <li key={c} className="flex items-center gap-2 text-sm text-steel">
                                            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                                            {c}
                                        </li>
                                    ))}
                                </ul>
                                <div className="mt-auto flex flex-col gap-2.5 pt-7">
                                    <Link
                                        to={`/contact?size=${t.size.toLowerCase()}#quote`}
                                        data-testid={`book-size-${t.size.toLowerCase()}`}
                                        className="btn-primary w-full !py-3 text-[13px]"
                                    >
                                        Book this size
                                        <ArrowUpRight className="h-4 w-4" />
                                    </Link>
                                    <a
                                        href={waLink(
                                            `Hello OSM Cargo Movers, I need a ${t.name}. Please share availability and rate.`
                                        )}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`whatsapp-size-${t.size.toLowerCase()}`}
                                        className="inline-flex w-full items-center justify-center gap-2 rounded-full border-2 border-[#1FA855] px-6 py-2.5 text-[13px] font-semibold text-[#178a45] transition-all hover:bg-[#1FA855] hover:text-white"
                                    >
                                        <MessageCircle className="h-4 w-4" />
                                        WhatsApp for {t.size}
                                    </a>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="bg-sand py-20 lg:py-28" data-testid="trailers-locations-section">
                <div className="container-x">
                    <SectionHead
                        eyebrow="Locations"
                        title="Where we place trailers"
                        hindi="Availability across major Indian cities"
                    />
                    <Reveal delay={0.1}>
                        <div className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="hubs-grid">
                            {HUBS.map((h) => (
                                <div
                                    key={h.city}
                                    className="flex items-center gap-3 rounded-xl border border-line bg-white px-4 py-3.5 transition-colors hover:border-brand"
                                    data-testid={`hub-card-${h.city.split(",")[0].split(" ")[0].toLowerCase()}`}
                                >
                                    <MapPin
                                        className={`h-4 w-4 shrink-0 ${h.hq ? "text-brand" : "text-steel"}`}
                                    />
                                    <span className="text-sm font-semibold text-ink">
                                        {h.city}
                                        {h.hq && (
                                            <span className="ml-2 rounded-full bg-brand px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                                                HQ
                                            </span>
                                        )}
                                    </span>
                                </div>
                            ))}
                        </div>
                        <p className="mt-6 text-sm text-fog">
                            Serving Pan India — the list above covers our regular placement hubs.
                            For any other location, call us: trailers keep moving every day.
                        </p>
                    </Reveal>

                    <Reveal delay={0.15}>
                        <div className="mt-10 flex flex-wrap gap-3">
                            <a
                                href={`tel:${SITE.phones[0].tel}`}
                                data-testid="trailers-call-btn"
                                className="btn-outline"
                            >
                                <Phone className="h-4 w-4" /> {SITE.phones[0].display}
                            </a>
                            <a
                                href={`tel:${SITE.phones[1].tel}`}
                                data-testid="trailers-call-btn-2"
                                className="btn-outline"
                            >
                                <Phone className="h-4 w-4" /> {SITE.phones[1].display}
                            </a>
                        </div>
                    </Reveal>
                </div>
            </section>

            <CtaBand title="Check today's availability" hindi="Call to check today's availability." />
        </>
    );
}
